import { Checkbox, Dropdown, MenuButton as JoyMenuButton, MenuItem, Typography } from '@mui/joy';
import { ListPlus } from '@phosphor-icons/react';
import { ColDef, Column } from 'ag-grid-community';
import { has, mapKeys } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'core/components';
import { useAgGridClientTableContext } from 'core/states';
import { Color, Level, Size, Variant } from 'theme/enums';

import { Label, LabelMenuItem, Menu } from './style';

interface IGroupOption {
  label: string;
  value: string;
}

export const GroupByButton = () => {
  const { t } = useTranslation();

  const { gridApi, rowGroupColumns = [] } = useAgGridClientTableContext();

  const groupByMenuItems = useMemo(() => {
    const columnDefs = gridApi?.getColumnDefs() || [];

    return columnDefs
      .filter((colDef: ColDef) => colDef.enableRowGroup)
      .map((colDef: ColDef) => ({ label: colDef.headerName, value: colDef.colId }));
  }, [gridApi]);

  const rowGroupColumnsMapped = useMemo(
    () => mapKeys(rowGroupColumns, (column: Column) => column.getColId()),
    [rowGroupColumns]
  );

  const onGroupByColumn = (colId: string, group: boolean) =>
    gridApi?.applyColumnState({ state: [{ colId, rowGroup: group }] });

  const onResetGroupBy = () => {
    gridApi?.applyColumnState({
      state: rowGroupColumns.map((column) => ({ colId: column.getColId(), rowGroup: false })),
    });
  };

  const onGroupByMenuItemClick = (option: IGroupOption) => {
    const isGroupedBy = has(rowGroupColumnsMapped, option?.value);

    onGroupByColumn(option?.value, !isGroupedBy);
  };

  const isGroupByApplied = rowGroupColumns.length > 0;

  return (
    <Dropdown>
      <JoyMenuButton
        color={isGroupByApplied ? Color.SUCCESS : Color.NEUTRAL}
        variant={isGroupByApplied ? Variant.SOFT : Variant.OUTLINED}
        startDecorator={<ListPlus />}
        endDecorator={null}
        disabled={groupByMenuItems.length === 0}
        sx={{
          ...(isGroupByApplied && {
            border: '1px solid',
            borderColor: 'neutral.softOutlined',
          }),
        }}
      >
        {isGroupByApplied
          ? t('buttonLabelGroupByApplied', { count: rowGroupColumns.length })
          : t('buttonLabelGroupBy')}
      </JoyMenuButton>

      <Menu placement="bottom-end" size={Size.SM}>
        <LabelMenuItem>
          <Label level={Level.TITLE_MD} color={Color.NEUTRAL}>
            {t('labelGroupBy')}
          </Label>
          {isGroupByApplied && (
            <Button
              color={Color.DANGER}
              variant={Variant.PLAIN}
              text={t('buttonLabelResetAll')}
              onClick={onResetGroupBy}
            />
          )}
        </LabelMenuItem>

        {groupByMenuItems.map((option: IGroupOption) => {
          const isSelected = has(rowGroupColumnsMapped, option?.value);

          return (
            <MenuItem key={option.value} onClick={() => onGroupByMenuItemClick(option)}>
              <Checkbox checked={isSelected} />
              <Typography level={Level.TITLE_MD} color={isSelected ? null : Color.NEUTRAL}>
                {option.label}
              </Typography>
            </MenuItem>
          );
        })}
      </Menu>
    </Dropdown>
  );
};
